import { Directive, ElementRef, Input, NgZone, OnDestroy, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[sd-control-value-accessor], [wctSdControlValueAccessor]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SdControlValueAccessorDirective),
      multi: true,
    },
  ],
})
export class SdControlValueAccessorDirective<TComponent = object> implements ControlValueAccessor, OnDestroy {
  @Input({ required: false }) public useRawValue?: boolean;
  @Input({ required: false }) public allowWriteNullOrUndefined?: boolean = true;

  public readonly elememtRef: ElementRef<HTMLElement & { value: unknown } & TComponent> = inject(ElementRef);

  protected onChangeFn!: (arg: unknown) => void;
  protected onTouchedFn!: () => void;

  protected readonly zone: NgZone = inject(NgZone);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected get _value(): any {
    return this.elememtRef.nativeElement.value;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private set _value(value: any) {
    this.elememtRef.nativeElement.value = value;
  }

  public ngOnDestroy(): void {
    this.zone.runOutsideAngular(() => {
      this.elememtRef.nativeElement.removeEventListener('sd-blur', this.handleSdBlur);
      this.elememtRef.nativeElement.removeEventListener('sd-input', this.handleSdChange);
      this.elememtRef.nativeElement.removeEventListener('sd-change', this.handleSdChange);
    });
  }

  public writeValue(obj: unknown): void {
    if ((obj === null || obj === undefined) && !this.allowWriteNullOrUndefined) {
      return;
    }

    this._value = this.useRawValue ? obj : `${obj ?? ''}`;
  }

  public registerOnChange(fn: (arg: unknown) => void): void {
    this.zone.runOutsideAngular(() => {
      this.elememtRef.nativeElement.addEventListener('sd-change', this.handleSdChange);
      this.elememtRef.nativeElement.addEventListener('sd-input', this.handleSdChange);
    });

    this.onChangeFn = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.zone.runOutsideAngular(() => {
      this.elememtRef.nativeElement.addEventListener('sd-blur', this.handleSdBlur);
    });

    this.onTouchedFn = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.elememtRef.nativeElement.setAttribute('disabled', '');
    } else {
      this.elememtRef.nativeElement.removeAttribute('disabled');
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected readonly handleSdChange = (event: any): void => {
    this.zone.run(() => {
      this.onChangeFn(event.target.value);
    });
  };

  protected readonly handleSdBlur = (): void => {
    this.zone.run(() => {
      this.onTouchedFn();
    });
  };
}
